/**
 * The "Quoted vs actual" tab on a completed earthworks job (EW-8,
 * GET /earthworks/jobs/:id/quoted-vs-actual). The job's sold lines against its
 * daily logs, loads, dump tickets and approved tickets. Every figure, variance
 * and rate is the API's. Links to the job type's summary, where the office
 * reviews suggestions for the AI-quote defaults.
 */
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useEwQvaJob } from '../../../hooks/useEwQva';
import { fmtMoney } from '../../../lib/earthworks';
import type { EwQvaJob, EwQvaMetric } from '../../../types/ewQva';
import { EwEmpty, EwPanel, EwRegister, EW_SECONDARY, type EwColumn } from '../EwBits';
import { QvaNotes, QvaPairStat, VariancePct, fmtMetric, fmtRate, volumeWord } from './EwQvaBits';

type BilledRow = EwQvaJob['billedByKind'][number] & { id: string };

export function EwQvaJobTab({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const query = useEwQvaJob(jobId);
  const d = query.data ?? null;
  const cur = d?.currency ?? null;
  const metrics: Array<EwQvaMetric & { id: string }> = (d?.metrics ?? []).map((m) => ({ ...m, id: m.key }));
  const billed: BilledRow[] = (d?.billedByKind ?? []).map((b) => ({ ...b, id: b.kind }));
  const vol = d ? volumeWord(d.unitSystem) : 'm³';

  const columns: EwColumn<EwQvaMetric & { id: string }>[] = [
    { key: 'label', header: 'Figure', render: (m) => (<><p className="font-bold text-white">{m.label}</p>{m.note ? <p className="mt-0.5 text-xs text-fieldiq-muted">{m.note}</p> : null}</>) },
    { key: 'quoted', header: 'Quoted', align: 'right', render: (m) => fmtMetric(m.quoted, m.unit, cur) },
    { key: 'actual', header: 'Actual', align: 'right', render: (m) => fmtMetric(m.actual, m.unit, cur) },
    { key: 'variance', header: 'Variance', align: 'right', render: (m) => fmtMetric(m.variance, m.unit, cur) },
    { key: 'pct', header: '%', align: 'right', render: (m) => <VariancePct pct={m.variancePct} testId={`ew-qva-pct-${m.key}`} /> },
  ];
  const billedColumns: EwColumn<BilledRow>[] = [
    { key: 'label', header: 'Kind', render: (b) => <span className="font-bold text-white">{b.label}</span> },
    { key: 'quoted', header: 'Quoted', align: 'right', render: (b) => fmtMoney(b.quoted, cur) },
    { key: 'billed', header: 'Billed', align: 'right', render: (b) => fmtMoney(b.billed, cur) },
    { key: 'variance', header: 'Variance', align: 'right', render: (b) => fmtMoney(b.variance, cur) },
  ];

  return (
    <div className="space-y-4" data-testid="ew-qva-job-tab">
      {d && <QvaNotes notes={d.notes} />}
      {d && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="ew-qva-job-rates">
          <QvaPairStat label={`Machine hours per ${vol}`} quoted={fmtRate(d.rates.machineHoursPerVolume.quoted, `h/${vol}`)} actual={fmtRate(d.rates.machineHoursPerVolume.actual, `h/${vol}`)} />
          <QvaPairStat label={`${vol} per load`} quoted={fmtRate(d.rates.volumePerLoad.quoted, vol)} actual={fmtRate(d.rates.volumePerLoad.actual, vol)} />
          <QvaPairStat label="Machine hours" quoted={fmtMetric(d.rates.machineHours.quoted, 'hour', cur)} actual={fmtMetric(d.rates.machineHours.actual, 'hour', cur)} />
          <QvaPairStat label="Crew hours" quoted={fmtMetric(d.rates.crewHours.quoted, 'hour', cur)} actual={fmtMetric(d.rates.crewHours.actual, 'hour', cur)} />
        </div>
      )}
      <EwPanel
        eyebrow="Quoted vs actual"
        title={d?.job.quoteNumber ? `Against ${d.job.quoteNumber}` : 'Against the sold lines'}
        testId="ew-qva-job-metrics"
        footer={d ? `${d.counts.dailyLogs} daily log${d.counts.dailyLogs === 1 ? '' : 's'} · ${d.counts.approvedTickets} approved ticket${d.counts.approvedTickets === 1 ? '' : 's'} · ${d.counts.dumpTickets} dump ticket${d.counts.dumpTickets === 1 ? '' : 's'}` : undefined}
      >
        <EwRegister
          query={query}
          rows={metrics}
          columns={columns}
          rail={(m) => ((m.variancePct ?? 0) > 0 ? 'border-l-fi-amber' : 'border-l-transparent')}
          subject="quoted vs actual"
          testId="ew-qva-metric"
          minWidth="min-w-[760px]"
          empty={<EwEmpty>Nothing is logged or quoted on this job yet.</EwEmpty>}
          card={(m) => (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-bold text-white">{m.label}</p>
                <VariancePct pct={m.variancePct} />
              </div>
              <p className="mt-1 font-mono text-sm text-fieldiq-text">{fmtMetric(m.quoted, m.unit, cur)} → {fmtMetric(m.actual, m.unit, cur)}</p>
            </>
          )}
        />
      </EwPanel>
      {billed.length > 0 && (
        <EwPanel eyebrow="Billing" title="Sold vs billed by kind" testId="ew-qva-job-billed">
          <EwRegister
            query={query}
            rows={billed}
            columns={billedColumns}
            rail={() => 'border-l-transparent'}
            subject="billing by kind"
            testId="ew-qva-billed"
            minWidth="min-w-[640px]"
            empty={<EwEmpty>Nothing billed.</EwEmpty>}
            card={(b) => (<><p className="text-sm font-bold text-white">{b.label}</p><p className="mt-1 font-mono text-sm">{fmtMoney(b.quoted, cur)} → {fmtMoney(b.billed, cur)}</p></>)}
          />
        </EwPanel>
      )}
      {d && (
        <button type="button" className={EW_SECONDARY} onClick={() => navigate(`/earthworks/quoted-vs-actual/${encodeURIComponent(d.job.jobType)}`)} data-testid="ew-qva-open-type">
          All {d.job.jobTypeLabel.toLowerCase()} jobs
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
